import React from "react";
import { ClampAlert } from "@/features/betslip/components/parts/ClampAlert";
import {
  useHasPermission,
  usePermission,
} from "@/features/auth/hooks/usePermission";
import { useVerifiedPermissions } from "@/features/auth/hooks/useVerifiedPermissions";
import { Clamp, ClockIcon, LockIcon, LocationBlockIcon } from "@picklebet/ui";
import { useHelpCenterStore } from "@/features/help-center/stores/helpCenterStore";
import { Link } from "@/features/shell/components/shared/Link/Link";
import * as styles from "./PickSlipUnauthorizedAlerts.module.scss";

const CooldownAlert = () => {
  return (
    <ClampAlert
      icon={<ClockIcon />}
      color={`danger`}
      clamp={
        <Clamp asChild variant={`danger`}>
          <Link to={`/preferences/account-deactivations`} noStyles>
            Learn More
          </Link>
        </Clamp>
      }
    >
      Your account is in Cooldown
    </ClampAlert>
  );
};

const ReopeningAlert = () => {
  return (
    <ClampAlert
      icon={<LockIcon />}
      color={`danger`}
      clamp={
        <Clamp asChild variant={`danger`}>
          <Link to={`/account/reopen`} noStyles>
            Reopen
          </Link>
        </Clamp>
      }
    >
      Your account requires reopening
    </ClampAlert>
  );
};

const UnderReviewAlert = () => {
  return (
    <ClampAlert
      icon={<LocationBlockIcon />}
      color={`danger`}
      clamp={
        <Clamp
          className={styles.clickable}
          onClick={() => useHelpCenterStore.getState().openHelpCenterChat()}
          variant={`danger`}
        >
          Contact Support
        </Clamp>
      }
    >
      Your account is under review
    </ClampAlert>
  );
};

const UnverifiedAccountAlert = () => {
  return (
    <ClampAlert
      icon={<LockIcon />}
      color={`danger`}
      clamp={
        <Clamp asChild variant={`danger`}>
          <Link to={`/account/verification`} noStyles>
            Verify
          </Link>
        </Clamp>
      }
    >
      Your account requires verification
    </ClampAlert>
  );
};

const BlockedLocationAlert = () => {
  const viewCasino = usePermission("viewCasino");
  const playCasino = usePermission("playPaidCasino");
  const canSubmitBet = useHasPermission("submitBet");

  const casinoAvailable = viewCasino === "GRANTED" && playCasino === "GRANTED";

  return (
    <ClampAlert
      icon={<LocationBlockIcon />}
      color={`danger`}
      clamp={
        casinoAvailable ? (
          <Clamp asChild variant={`danger`}>
            <Link to={`/casino/lobby`} noStyles>
              Try Casino
            </Link>
          </Clamp>
        ) : canSubmitBet ? (
          <Clamp asChild variant={`danger`}>
            <Link to={`/sports/betting`} noStyles>
              Try Betting
            </Link>
          </Clamp>
        ) : null
      }
    >
      {"Pick'ems is not available in your location"}
    </ClampAlert>
  );
};

const PickSlipUnauthorizedAlerts = () => {
  const {
    permissions: { submitPickems },
    isSettling,
  } = useVerifiedPermissions("submitPickems");

  if (submitPickems === "GRANTED") {
    return null;
  }

  if (submitPickems === "COOLDOWN") {
    return <CooldownAlert />;
  }

  if (submitPickems === "ACCOUNT_REOPEN_REQUIRED") {
    return <ReopeningAlert />;
  }

  if (submitPickems === "BLOCKED") {
    return <UnderReviewAlert />;
  }

  if (
    submitPickems === "PHYSICAL_LOCATION_UNSUPPORTED" ||
    submitPickems === "RESIDENTIAL_LOCATION_UNSUPPORTED"
  ) {
    return <BlockedLocationAlert />;
  }

  if (submitPickems === "VERIFICATION_STATUS_UNSUPPORTED") {
    // permissions may still be catching up
    if (isSettling) return null;

    return <UnverifiedAccountAlert />;
  }
  return (
    <ClampAlert color={`danger`}>
      Sorry you are unable to play Pickems at this time.
    </ClampAlert>
  );
};

export { PickSlipUnauthorizedAlerts };
